/**
 * WO-D5 — "+ Add material" quick-pick (surfaces the existing recommendMaterials recommender).
 *
 * Pushed OVER the tabs on the (app) stack from the estimate builder with the estimate id as a
 * search param (?estimateId=…) — opaque UUID, no PII in URL (CLAUDE.md §2). Lists the org's RANKED
 * recommended materials (GET /api/materials/suggested); tapping one appends an editable material
 * line to the estimate via POST /api/estimates/:id/lines (the existing addLineItem path), priced at
 * the material's preferred-supplier cost when known, then returns to the builder where qty/price are
 * fully editable.
 *
 * States: loading / error / empty (no materials). Every control is a real backend round-trip via
 * TanStack Query. NO mock data, no no-op handlers. NEVER imports @smartbox/* (invariant 1).
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listSuggestedMaterials } from '../../../src/api/services';
import { addLineItem } from '../../../src/api/estimates';
import { Screen, Text, Card, Button, ListRow } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage, formatCurrency } from '../../../src/lib/format';
import type { SuggestedMaterialDto } from '@beam/api-types';

export default function AddMaterialScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { estimateId } = useLocalSearchParams<{ estimateId: string }>();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const materialsQuery = useQuery({
    queryKey: ['suggested-materials'],
    queryFn: () => listSuggestedMaterials(20),
    staleTime: 30_000,
  });

  const addMutation = useMutation({
    mutationFn: (m: SuggestedMaterialDto) =>
      addLineItem(estimateId!, {
        lineType: 'material',
        description: m.name,
        quantity: 1,
        materialId: m.materialId,
        ...(m.unitCost != null ? { unitPrice: parseFloat(m.unitCost) } : {}),
      }),
    onSuccess: (estimate) => {
      queryClient.setQueryData(['estimates', estimateId], estimate);
      void queryClient.invalidateQueries({ queryKey: ['estimates'] });
      router.back();
    },
    onSettled: () => setPendingId(null),
  });

  if (!estimateId) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No estimate to add a material to.
          </Text>
        </View>
      </Screen>
    );
  }

  const materials: SuggestedMaterialDto[] = materialsQuery.data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Add a material</Text>
      <Text variant="body" color="secondary">
        Pick from your most-used materials. We'll add an editable line with its current price.
      </Text>

      {materialsQuery.isLoading ? (
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading materials"
          />
        </View>
      ) : materialsQuery.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(materialsQuery.error, 'Could not load materials.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => materialsQuery.refetch()}
          />
        </Card>
      ) : materials.length === 0 ? (
        <Card>
          <Text variant="body" color="secondary">
            No materials to suggest yet. As you build estimates and import history, your most-used
            materials show up here.
          </Text>
        </Card>
      ) : (
        <View style={styles.list}>
          {materials.map((m, index) => {
            const busy = addMutation.isPending && pendingId === m.materialId;
            const anyBusy = addMutation.isPending;
            const subtitle = [
              m.unitCost != null ? formatCurrency(m.unitCost) : 'No price yet',
              m.usageCount > 0 ? `used ${m.usageCount}×` : null,
              m.hasPreferredSupplier ? 'preferred supplier' : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ListRow
                key={m.materialId}
                testID={`suggested-material-${index}`}
                title={m.name}
                subtitle={subtitle}
                right={
                  busy ? (
                    <ActivityIndicator
                      color={theme.color.action.primary}
                      accessibilityLabel={`Adding ${m.name}`}
                    />
                  ) : (
                    <Text variant="label" color="link">
                      Add
                    </Text>
                  )
                }
                disabled={anyBusy}
                onPress={() => {
                  setPendingId(m.materialId);
                  addMutation.mutate(m);
                }}
                accessibilityLabel={`Add material ${m.name}`}
              />
            );
          })}
        </View>
      )}

      {addMutation.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(addMutation.error, 'Could not add that material.')}
          </Text>
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: { paddingVertical: theme.spacing['2xl'], alignItems: 'center' },
  list: { gap: theme.spacing.sm },
});
